import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { AnyFormLayout, LAYOUT_BY_TYPE } from '../constants/blockByType'
import { OccasionalSlotFormProps } from '../types'
import { useOccasionalSlotFormState } from './useOccasionalSlotFormState'
import { useOccasionalSlotFormHandlers } from './useOccasionalSlotFormHandlers'
import { useOccasionalSlotFormOptions } from './useOccasionalSlotFormOptions'
import { useOccasionalSlotFormSubmission } from './useOccasionalSlotFormSubmission'
import { useOccasionalSlotConfirmation } from './useOccasionalSlotConfirmation'

export function useOccasionalSlotForm(props: OccasionalSlotFormProps) {
    const { type } = props
    const { currentDaycare, isRpeOrLaep } = useCurrentDaycareInfos()
    const layout: AnyFormLayout = LAYOUT_BY_TYPE[type]
    const state: ReturnType<typeof useOccasionalSlotFormState> = useOccasionalSlotFormState(props, currentDaycare)
    const handlers: ReturnType<typeof useOccasionalSlotFormHandlers> = useOccasionalSlotFormHandlers(
        type,
        layout,
        state
    )
    const options: ReturnType<typeof useOccasionalSlotFormOptions> = useOccasionalSlotFormOptions(
        props,
        currentDaycare,
        state
    )

    const { handleConfirm, loading } = useOccasionalSlotFormSubmission({
        formProps: props,
        daycare: currentDaycare,
        validation: {
            dates: state.dateSelection,
            start: state.startTime,
            end: state.endTime,
            sectionId: state.selectedSectionId,
            count: state.occasionalSlotNumber,
            confirmedChildIds: state.confirmedChildIds
        },
        slotDetails: {
            childrenPriority: state.priorityChildIds,
            groupPriority: state.priorityGroupIds,
            scheduled: state.schedule,
            expiryChoice: state.expiration,
            internalNote: state.internalNoteValue,
            externalNote: state.externalNoteValue,
            externalIcon: state.externalIcon,
            placeId: state.placeId
        },
        onValidation: handlers.handleValidation,
        onScheduleRequest: () => openScheduleDialog()
    })

    const { confirmationDialog, handleFormConfirm, scheduleDialog, openScheduleDialog } = useOccasionalSlotConfirmation(
        {
            popup: layout.confirmPopUp,
            slotCount: {
                value: state.occasionalSlotNumber,
                onChange: handlers.handleSlotCountChange,
                min: Math.max(1, state.confirmedChildIds.length),
                max: type === 'multiEdit' ? props.occasionalSlots.length : 1
            },
            schedule: {
                value: state.schedule,
                onChange: state.setSchedule,
                maxDate: state.firstDate,
                daycare: currentDaycare,
                onConfirm: () => handleConfirm(true, true)
            },
            onConfirm: () => handleConfirm(false)
        }
    )

    return {
        ...state,
        ...handlers,
        ...options,
        currentDaycare,
        isRpeOrLaep,
        layout,
        handleConfirm,
        loading,
        scheduleDialog,
        confirmationDialog,
        handleFormConfirm
    }
}
